import { httpClient } from '@shared/infrastructure/http/http-client.js';
import type {
  IncoherenceMark,
  VideoLabelling,
  VideoLabellingState,
} from '../model.js';
import type { IncoherenceRange } from '../lib/incoherence-range.js';

interface RangePayload {
  id: number;
  start_ms: number;
  end_ms: number;
  labeled_by?: string | null;
  created_at?: string | null;
}

/** Virtual Profiler's labelling, passed through by the back-office unwrapped. */
interface LabellingPayload {
  source_id?: number | string | null;
  state?: string | null;
  complete?: boolean | null;
  completed_by?: string | null;
  completed_at?: string | null;
  ranges?: RangePayload[] | null;
}

const STATES: ReadonlySet<string> = new Set<VideoLabellingState>([
  'incoherent',
  'coherent',
  'unlabelled',
]);

function toDate(value: string | null | undefined): Date | null {
  return value ? new Date(value) : null;
}

function toMark(payload: RangePayload): IncoherenceMark {
  return {
    id: Number(payload.id),
    startMs: payload.start_ms,
    endMs: payload.end_ms,
    labeledBy: payload.labeled_by ?? null,
    createdAt: toDate(payload.created_at),
  };
}

/**
 * The labelling in the screen's spelling, ranges in the order of the video.
 *
 * A state this build does not know is worked out from the ranges and the mark, as upstream works
 * it out: shown as a word nobody can read it would be no use to anybody.
 */
function toLabelling(payload: LabellingPayload, videoId: number): VideoLabelling {
  const ranges = (payload.ranges ?? [])
    .map(toMark)
    .sort((a, b) => a.startMs - b.startMs || a.id - b.id);
  const complete = payload.complete === true;
  const derived: VideoLabellingState =
    ranges.length > 0 ? 'incoherent' : complete ? 'coherent' : 'unlabelled';

  return {
    videoId:
      payload.source_id === null || payload.source_id === undefined
        ? videoId
        : Number(payload.source_id),
    state:
      typeof payload.state === 'string' && STATES.has(payload.state)
        ? (payload.state as VideoLabellingState)
        : derived,
    complete,
    completedBy: payload.completed_by ?? null,
    completedAt: toDate(payload.completed_at),
    ranges,
  };
}

/**
 * A write's answer: the whole labelling after it, or null when it came back without one — which
 * tells the caller to read it again rather than show a video with nothing.
 */
function toWritten(
  payload: LabellingPayload | null | undefined,
  videoId: number,
): VideoLabelling | null {
  return payload ? toLabelling(payload, videoId) : null;
}

function base(videoId: number): string {
  return `/virtual-profiler/videos/${videoId}/labelling`;
}

function body(range: IncoherenceRange): Record<string, number> {
  return { startMs: Math.round(range.startMs), endMs: Math.round(range.endMs) };
}

/**
 * Boundary with a video's incoherence labelling (HU-36, incoherence-only since 2026-10-14).
 *
 * Per video, never per run: the ranges are the ground truth the coherence engine is measured
 * against, so they survive every re-analysis. Only incoherences are written — there is no «verdad»
 * to send; a video with none is one marked «Completo» with no range.
 */
export const videoLabellingApi = {
  get: async (videoId: number): Promise<VideoLabelling> =>
    toLabelling((await httpClient.get<LabellingPayload>(base(videoId))) ?? {}, videoId),

  addRange: async (videoId: number, range: IncoherenceRange): Promise<VideoLabelling | null> =>
    toWritten(
      await httpClient.post<LabellingPayload>(`${base(videoId)}/ranges`, body(range)),
      videoId,
    ),

  updateRange: async (
    videoId: number,
    rangeId: number,
    range: IncoherenceRange,
  ): Promise<VideoLabelling | null> =>
    toWritten(
      await httpClient.put<LabellingPayload>(`${base(videoId)}/ranges/${rangeId}`, body(range)),
      videoId,
    ),

  removeRange: async (videoId: number, rangeId: number): Promise<VideoLabelling | null> =>
    toWritten(
      await httpClient.delete<LabellingPayload>(`${base(videoId)}/ranges/${rangeId}`),
      videoId,
    ),

  /** «Completo»: every incoherence of the video is marked — none, for a video with none. */
  setComplete: async (videoId: number, complete: boolean): Promise<VideoLabelling | null> =>
    toWritten(
      await httpClient.put<LabellingPayload>(`${base(videoId)}/complete`, { complete }),
      videoId,
    ),

  /**
   * The whole labelling in one write (2026-10-29): these ranges and this «Completo», nothing
   * else. What a step of the upload's labelling saves, instead of clearing and adding one by one.
   */
  replace: async (
    videoId: number,
    ranges: readonly IncoherenceRange[],
    complete: boolean,
  ): Promise<VideoLabelling | null> =>
    toWritten(
      await httpClient.put<LabellingPayload>(`${base(videoId)}/ranges`, {
        ranges: ranges.map(body),
        complete,
      }),
      videoId,
    ),

  /** Every range and the mark: the video goes back to «Sin etiquetar». */
  clear: (videoId: number): Promise<void> => httpClient.delete<void>(base(videoId)),

  /**
   * «Verdad»: no range and the labelling marked complete. One write when there are ranges to clear
   * (2026-10-29), so nothing on screen sees the «Sin etiquetar» in between.
   */
  markTruthful: async (videoId: number, clearFirst: boolean): Promise<VideoLabelling | null> =>
    toWritten(
      clearFirst
        ? await httpClient.put<LabellingPayload>(`${base(videoId)}/ranges`, {
            ranges: [],
            complete: true,
          })
        : await httpClient.put<LabellingPayload>(`${base(videoId)}/complete`, { complete: true }),
      videoId,
    ),
};
